import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';

interface GradingEvent {
  grading_event_id: number;
  grading_date: string;
  grading_event_desc: string;
  grading_type: string;
  status: string;
}

interface Registration {
  grading_id: number;
  profile_id: number;
  member_name: string;
  member_number: string;
  dojo_name: string;
  age_group_name: string;
  gender: string;
  current_rank_name: string;
  current_rank_index: number;
  target_rank_name: string;
  target_rank_index: number;
  approval_status: string;
  instructor_approved: number;
  payment_label: string;
  is_paid: number;
  grading_fee_paid: number;
  grading_fee_expected: number;
  grading_status: string;
}

interface BeltSummary {
  current_rank_name: string;
  current_rank_index: number;
  target_rank_name: string;
  target_rank_index: number;
  total: number;
  paid: number;
  unpaid: number;
  approved: number;
  children_count: number;
  teenager_count: number;
  adult_count: number;
  unknown_count: number;
}

interface AgeSummary {
  age_group_name: string;
  age_group_id: number;
  total: number;
  paid: number;
  unpaid: number;
}

interface ChaseRecord {
  grading_id: number;
  profile_id: number;
  member_name: string;
  member_number: string;
  dojo_name: string;
  current_rank_name: string;
  target_rank_name: string;
  age_group_name: string;
  grading_fee_expected: number;
}

type TabMode = 'belt' | 'age' | 'members' | 'chase';
const PAGE_SIZE = 50;

const Registration: React.FC = () => {
  const [events, setEvents] = useState<GradingEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<Registration[]>([]);
  const [beltSummary, setBeltSummary] = useState<BeltSummary[]>([]);
  const [ageSummary, setAgeSummary] = useState<AgeSummary[]>([]);
  const [chaseList, setChaseList] = useState<ChaseRecord[]>([]);
  const [dojos, setDojos] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabMode>('belt');
  const [page, setPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [selectedDojos, setSelectedDojos] = useState<string[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<string[]>([]);
  const [selectedApproval, setSelectedApproval] = useState<string[]>([]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const res = await api.get('/reports/gradings/registration');
        const data = res.data.data;
        setEvents(data.events || []);
      } catch (err) {
        setError('Failed to load grading events');
      } finally {
        setLoadingEvents(false);
      }
    };
    loadEvents();
  }, []);

  const loadRegistration = useCallback(async (eventId: number) => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/reports/gradings/registration?eventId=' + eventId);
      const data = res.data.data;
      setRegistrations(data.registrations || []);
      setFilteredRegistrations(data.registrations || []);
      setBeltSummary(data.beltSummary || []);
      setAgeSummary(data.ageSummary || []);
      setChaseList(data.chaseList || []);
      setDojos(
        Array.from(
          new Set((data.registrations || []).map((r: Registration) => r.dojo_name))
        ).sort() as string[]
      );
    } catch (err) {
      setError('Failed to load registration data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      loadRegistration(selectedEventId);
      setSearch('');
      setSelectedDojos([]);
      setSelectedPayment([]);
      setSelectedApproval([]);
      setPage(1);
      setTab('belt');
    }
  }, [selectedEventId, loadRegistration]);

  useEffect(() => {
    let result = registrations;

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          (r.member_name || '').toLowerCase().includes(s) ||
          (r.member_number || '').toLowerCase().includes(s) ||
          (r.current_rank_name || '').toLowerCase().includes(s) ||
          (r.target_rank_name || '').toLowerCase().includes(s)
      );
    }

    if (selectedDojos.length > 0) result = result.filter((r) => selectedDojos.includes(r.dojo_name));
    if (selectedPayment.length > 0) result = result.filter((r) => selectedPayment.includes(r.payment_label));
    if (selectedApproval.length > 0) result = result.filter((r) => selectedApproval.includes(r.approval_status));

    setFilteredRegistrations(result);
    setPage(1);
  }, [search, selectedDojos, selectedPayment, selectedApproval, registrations]);

  const toggleMulti = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const hasFilters = search || selectedDojos.length > 0 || selectedPayment.length > 0 || selectedApproval.length > 0;

  const clearFilters = () => {
    setSearch('');
    setSelectedDojos([]);
    setSelectedPayment([]);
    setSelectedApproval([]);
    setPage(1);
  };

  const totalPages = Math.ceil(filteredRegistrations.length / PAGE_SIZE);
  const paginated = filteredRegistrations.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedEvent = events.find((e) => e.grading_event_id === selectedEventId);

  const totalRegistered = registrations.length;
  const totalPaid = registrations.filter((r) => r.is_paid === 1).length;
  const totalUnpaid = registrations.filter((r) => r.is_paid === 0).length;
  const totalApproved = registrations.filter((r) => r.instructor_approved === 1).length;
  const totalChase = chaseList.length;
  const totalFeeCollected = registrations
    .filter((r) => r.is_paid === 1)
    .reduce((sum, r) => sum + Number(r.grading_fee_paid || 0), 0);

  const exportExcel = () => {
    setExporting(true);
    try {
      let headers: string[] = [];
      let rows: any[][] = [];

      if (tab === 'belt') {
        headers = ['Current Belt', 'Target Belt', 'Total', 'Paid', 'Unpaid', 'Approved', 'Children', 'Teenager', 'Adult', 'Unknown'];
        rows = beltSummary.map((b) => [
          b.current_rank_name, b.target_rank_name,
          b.total, b.paid, b.unpaid, b.approved,
          b.children_count || 0, b.teenager_count || 0, b.adult_count || 0, b.unknown_count || 0,
        ]);
      } else if (tab === 'age') {
        headers = ['Age Group', 'Total', 'Paid', 'Unpaid'];
        rows = ageSummary.map((a) => [a.age_group_name || 'Unknown', a.total, a.paid, a.unpaid]);
      } else if (tab === 'members') {
        headers = ['Member No.', 'Member Name', 'Dojo', 'Current Belt', 'Target Belt', 'Age Group', 'Gender', 'Payment', 'Approval', 'Fee Expected (₦)', 'Fee Paid (₦)'];
        rows = filteredRegistrations.map((r) => [
          r.member_number || '', r.member_name, r.dojo_name,
          r.current_rank_name, r.target_rank_name,
          r.age_group_name || '', r.gender || '',
          r.payment_label, r.approval_status,
          r.grading_fee_expected || '', r.grading_fee_paid || '',
        ]);
      } else {
        headers = ['Member No.', 'Member Name', 'Dojo', 'Current Belt', 'Target Belt', 'Age Group', 'Fee Expected (₦)'];
        rows = chaseList.map((c) => [
          c.member_number || '', c.member_name, c.dojo_name,
          c.current_rank_name, c.target_rank_name,
          c.age_group_name || '', c.grading_fee_expected || '',
        ]);
      }

      const bom = '\uFEFF';
      const csv = bom + [headers, ...rows]
        .map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','))
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NASK_Registration_' + tab + '_' +
        (selectedEvent?.grading_event_desc || '') + '_' +
        new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const MultiSelect: React.FC<{
    label: string;
    id: string;
    options: string[];
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  }> = ({ label, id, options, selected, setSelected }) => {
    const isOpen = openDropdown === id;
    return (
      <div style={styles.filterGroup}>
        <label style={styles.filterLabel}>{label}</label>
        <div style={{ position: 'relative' }}>
          <button
            onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : id); }}
            style={{ ...styles.multiSelectBtn, borderColor: selected.length > 0 ? '#cc0000' : '#3a3a3a' }}
          >
            <span>
              {selected.length === 0 ? 'All' :
                selected.length === 1 ? selected[0] :
                  selected.length + ' selected'}
            </span>
            <span style={{ fontSize: '10px' }}>▼</span>
          </button>
          {isOpen && (
            <div style={styles.multiSelectDropdown} onClick={(e) => e.stopPropagation()}>
              {options.map((opt) => (
                <label key={opt} style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={selected.includes(opt)}
                    onChange={() => toggleMulti(opt, setSelected)}
                    style={{ marginRight: '8px', accentColor: '#cc0000' }}
                  />
                  {opt}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container} onClick={() => setOpenDropdown(null)}>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Grading Registration</h1>
          <p style={styles.subtitle}>
            {selectedEvent
              ? selectedEvent.grading_event_desc + ' — ' +
                new Date(selectedEvent.grading_date).toLocaleDateString('en-GB')
              : 'Select a grading event'}
          </p>
        </div>
        {selectedEventId && (
          <button
            onClick={exportExcel}
            style={exporting ? styles.exportBtnDisabled : styles.exportBtn}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : '⬇️ Export to Excel'}
          </button>
        )}
      </div>

      {/* Event Selector */}
      <div style={styles.eventSelector}>
        <label style={styles.filterLabel}>Select Grading Event</label>
        {loadingEvents ? (
          <div style={{ color: '#888888', fontSize: '14px' }}>Loading events...</div>
        ) : (
          <select
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(e.target.value ? parseInt(e.target.value) : null)}
            style={styles.eventSelect}
          >
            <option value="">— Choose a grading event —</option>
            {events.map((e) => (
              <option key={e.grading_event_id} value={e.grading_event_id}>
                {new Date(e.grading_date).toLocaleDateString('en-GB')} — {e.grading_event_desc} ({e.grading_type})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={styles.errorBox}>
          {error}
          {selectedEventId && (
            <button onClick={() => loadRegistration(selectedEventId)} style={styles.retryBtn}>Retry</button>
          )}
        </div>
      )}

      {/* No event selected */}
      {!selectedEventId && !loadingEvents && (
        <div style={styles.noEventBox}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🥋</div>
          <p style={{ color: '#888888', fontSize: '16px' }}>
            Select a grading event above to view registration data
          </p>
        </div>
      )}

      {/* Content */}
      {selectedEventId && (
        loading ? (
          <div style={styles.loadingBox}>Loading registration data...</div>
        ) : (
          <>
            {/* Stats Cards */}
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Total Registered</p>
                <p style={{ ...styles.statValue, color: '#3b82f6' }}>{totalRegistered}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Paid</p>
                <p style={{ ...styles.statValue, color: '#22c55e' }}>{totalPaid}</p>
                <p style={styles.statSub}>
                  {totalRegistered > 0
                    ? Math.round((totalPaid / totalRegistered) * 100) + '% of registered'
                    : '0%'}
                </p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Unpaid</p>
                <p style={{ ...styles.statValue, color: '#f59e0b' }}>{totalUnpaid}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Instructor Approved</p>
                <p style={{ ...styles.statValue, color: '#6b8cff' }}>{totalApproved}</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Approved Not Paid</p>
                <p style={{ ...styles.statValue, color: '#cc0000' }}>{totalChase}</p>
                <p style={styles.statSub}>Chase list</p>
              </div>
              <div style={styles.statCard}>
                <p style={styles.statLabel}>Fees Collected</p>
                <p style={{ ...styles.statValue, color: '#22c55e' }}>
                  {'₦' + totalFeeCollected.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div style={styles.tabs}>
              {[
                { id: 'belt', label: '🥋 By Belt' },
                { id: 'age', label: '👤 By Age Group' },
                { id: 'members', label: '👥 Member List' },
                { id: 'chase', label: '⚠️ Chase List (' + totalChase + ')' },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id as TabMode)}
                  style={tab === t.id ? styles.tabActive : styles.tab}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Belt Summary Tab */}
            {tab === 'belt' && (
              <>
                <p style={{ color: '#666666', fontSize: '12px', marginBottom: '12px' }}>
                  * Where Current Belt = Target Belt, the member is registered for a resit attempt.
                </p>
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {[
                          'Current Belt', 'Target Belt',
                          'Total', 'Paid', 'Unpaid', 'Approved',
                          'Children', 'Teenager', 'Adult', 'Unknown',
                        ].map((h) => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {beltSummary.map((b, i) => (
                        <tr
                          key={b.current_rank_name + '_' + b.target_rank_name}
                          style={i % 2 === 0 ? styles.trEven : styles.trOdd}
                        >
                          <td style={styles.td}>
                            <span style={styles.rankBadge}>{b.current_rank_name}</span>
                            {b.current_rank_name === b.target_rank_name && (
                              <span style={{ fontSize: '10px', color: '#f59e0b', marginLeft: '6px' }}>
                                resit
                              </span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <span style={styles.targetBadge}>{b.target_rank_name}</span>
                          </td>
                          <td style={styles.tdCentre}>{Number(b.total)}</td>
                          <td style={{ ...styles.tdCentre, color: '#22c55e' }}>{Number(b.paid)}</td>
                          <td style={{ ...styles.tdCentre, color: '#f59e0b' }}>{Number(b.unpaid)}</td>
                          <td style={{ ...styles.tdCentre, color: '#6b8cff' }}>{Number(b.approved)}</td>
                          <td style={styles.tdCentre}>{Number(b.children_count) || 0}</td>
                          <td style={styles.tdCentre}>{Number(b.teenager_count) || 0}</td>
                          <td style={styles.tdCentre}>{Number(b.adult_count) || 0}</td>
                          <td style={{ ...styles.tdCentre, color: '#666666' }}>
                            {Number(b.unknown_count) || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={styles.footerRow}>
                        <td colSpan={2} style={{ ...styles.td, fontWeight: '700', color: '#ffffff' }}>
                          TOTAL
                        </td>
                        <td style={styles.tdCentre}>
                          {beltSummary.reduce((s, b) => s + Number(b.total), 0)}
                        </td>
                        <td style={{ ...styles.tdCentre, color: '#22c55e' }}>
                          {beltSummary.reduce((s, b) => s + Number(b.paid), 0)}
                        </td>
                        <td style={{ ...styles.tdCentre, color: '#f59e0b' }}>
                          {beltSummary.reduce((s, b) => s + Number(b.unpaid), 0)}
                        </td>
                        <td style={{ ...styles.tdCentre, color: '#6b8cff' }}>
                          {beltSummary.reduce((s, b) => s + Number(b.approved), 0)}
                        </td>
                        <td style={styles.tdCentre}>
                          {beltSummary.reduce((s, b) => s + Number(b.children_count || 0), 0)}
                        </td>
                        <td style={styles.tdCentre}>
                          {beltSummary.reduce((s, b) => s + Number(b.teenager_count || 0), 0)}
                        </td>
                        <td style={styles.tdCentre}>
                          {beltSummary.reduce((s, b) => s + Number(b.adult_count || 0), 0)}
                        </td>
                        <td style={{ ...styles.tdCentre, color: '#666666' }}>
                          {beltSummary.reduce((s, b) => s + Number(b.unknown_count || 0), 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {beltSummary.length === 0 && (
                    <div style={styles.emptyBox}>No registration data for this event.</div>
                  )}
                </div>
              </>
            )}

            {/* Age Group Summary Tab */}
            {tab === 'age' && (
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['Age Group', 'Total', 'Paid', 'Unpaid'].map((h) => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ageSummary.map((a, i) => (
                      <tr
                        key={a.age_group_name || 'unknown'}
                        style={i % 2 === 0 ? styles.trEven : styles.trOdd}
                      >
                        <td style={{ ...styles.td, fontWeight: '600', color: '#ffffff' }}>
                          {a.age_group_name || 'Unknown'}
                        </td>
                        <td style={styles.tdCentre}>{Number(a.total)}</td>
                        <td style={{ ...styles.tdCentre, color: '#22c55e' }}>{Number(a.paid)}</td>
                        <td style={{ ...styles.tdCentre, color: '#f59e0b' }}>{Number(a.unpaid)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={styles.footerRow}>
                      <td style={{ ...styles.td, fontWeight: '700', color: '#ffffff' }}>TOTAL</td>
                      <td style={styles.tdCentre}>
                        {ageSummary.reduce((s, a) => s + Number(a.total), 0)}
                      </td>
                      <td style={{ ...styles.tdCentre, color: '#22c55e' }}>
                        {ageSummary.reduce((s, a) => s + Number(a.paid), 0)}
                      </td>
                      <td style={{ ...styles.tdCentre, color: '#f59e0b' }}>
                        {ageSummary.reduce((s, a) => s + Number(a.unpaid), 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                {ageSummary.length === 0 && (
                  <div style={styles.emptyBox}>No registration data for this event.</div>
                )}
              </div>
            )}

            {/* Member List Tab */}
            {tab === 'members' && (
              <>
                <div style={styles.filtersCard} onClick={(e) => e.stopPropagation()}>
                  <div style={styles.filtersRow}>
                    <div style={styles.filterGroup}>
                      <label style={styles.filterLabel}>Search</label>
                      <input
                        type="text"
                        placeholder="Name, member no., rank..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={styles.searchInput}
                      />
                    </div>
                    <MultiSelect
                      label="Dojo" id="dojo"
                      options={dojos}
                      selected={selectedDojos}
                      setSelected={setSelectedDojos}
                    />
                    <MultiSelect
                      label="Payment" id="payment"
                      options={['Paid', 'Unpaid']}
                      selected={selectedPayment}
                      setSelected={setSelectedPayment}
                    />
                    <MultiSelect
                      label="Approval" id="approval"
                      options={['Approved', 'Not Approved']}
                      selected={selectedApproval}
                      setSelected={setSelectedApproval}
                    />
                    {hasFilters && (
                      <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>&nbsp;</label>
                        <button onClick={clearFilters} style={styles.clearBtn}>Clear All</button>
                      </div>
                    )}
                  </div>
                </div>

                <p style={{ color: '#888888', fontSize: '13px', marginBottom: '12px' }}>
                  {filteredRegistrations.length.toLocaleString()} member{filteredRegistrations.length !== 1 ? 's' : ''} found
                </p>

                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {[
                          'Member', 'Dojo', 'Current Belt', 'Target Belt',
                          'Age Group', 'Gender', 'Payment', 'Approval',
                          'Fee Expected', 'Fee Paid',
                        ].map((h) => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((r, i) => (
                        <tr key={r.grading_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}>
                            <div style={{ fontWeight: '500', color: '#ffffff' }}>{r.member_name}</div>
                            <div style={{ fontSize: '11px', color: '#666666' }}>{r.member_number || ''}</div>
                          </td>
                          <td style={styles.td}>{r.dojo_name}</td>
                          <td style={styles.td}>
                            <span style={styles.rankBadge}>{r.current_rank_name}</span>
                          </td>
                          <td style={styles.td}>
                            <span style={styles.targetBadge}>{r.target_rank_name}</span>
                          </td>
                          <td style={styles.td}>{r.age_group_name || '—'}</td>
                          <td style={styles.td}>
                            {r.gender
                              ? r.gender.charAt(0).toUpperCase() + r.gender.slice(1)
                              : '—'}
                          </td>
                          <td style={styles.td}>
                            <span style={r.is_paid === 1 ? styles.paidBadge : styles.unpaidBadge}>
                              {r.payment_label}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <span style={r.instructor_approved === 1 ? styles.approvedBadge : styles.notApprovedBadge}>
                              {r.approval_status}
                            </span>
                          </td>
                          <td style={styles.tdNum}>
                            {r.grading_fee_expected
                              ? '₦' + Number(r.grading_fee_expected).toLocaleString()
                              : '—'}
                          </td>
                          <td style={styles.tdNum}>
                            {r.is_paid === 1
                              ? '₦' + Number(r.grading_fee_paid).toLocaleString()
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredRegistrations.length === 0 && (
                    <div style={styles.emptyBox}>No members found.</div>
                  )}
                </div>

                {filteredRegistrations.length > PAGE_SIZE && (
                  <div style={styles.pagination}>
                    <span style={styles.pageInfo}>
                      {'Showing ' +
                        ((page - 1) * PAGE_SIZE + 1) +
                        '–' +
                        Math.min(page * PAGE_SIZE, filteredRegistrations.length) +
                        ' of ' +
                        filteredRegistrations.length}
                    </span>
                    <div style={styles.pageButtons}>
                      <button
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                        style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                      >First</button>
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={page === 1 ? styles.pageBtnDisabled : styles.pageBtn}
                      >Prev</button>
                      <span style={styles.pageNumber}>{'Page ' + page + ' of ' + totalPages}</span>
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                      >Next</button>
                      <button
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                        style={page === totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                      >Last</button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Chase List Tab */}
            {tab === 'chase' && (
              <>
                <div style={{
                  ...styles.warningBox,
                  marginBottom: '16px',
                }}>
                  ⚠️ These members have been approved by their instructor but have not yet paid their grading fee.
                </div>
                <div style={styles.tableWrapper}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        {['Member', 'Dojo', 'Current Belt', 'Target Belt', 'Age Group', 'Fee Expected'].map((h) => (
                          <th key={h} style={styles.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {chaseList.map((c, i) => (
                        <tr key={c.grading_id} style={i % 2 === 0 ? styles.trEven : styles.trOdd}>
                          <td style={styles.td}>
                            <div style={{ fontWeight: '500', color: '#ffffff' }}>{c.member_name}</div>
                            <div style={{ fontSize: '11px', color: '#666666' }}>{c.member_number || ''}</div>
                          </td>
                          <td style={styles.td}>{c.dojo_name}</td>
                          <td style={styles.td}>
                            <span style={styles.rankBadge}>{c.current_rank_name}</span>
                          </td>
                          <td style={styles.td}>
                            <span style={styles.targetBadge}>{c.target_rank_name}</span>
                          </td>
                          <td style={styles.td}>{c.age_group_name || '—'}</td>
                          <td style={styles.tdNum}>
                            {c.grading_fee_expected
                              ? '₦' + Number(c.grading_fee_expected).toLocaleString()
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {chaseList.length === 0 && (
                    <div style={{ ...styles.emptyBox, color: '#22c55e' }}>
                      🎉 All approved members have paid their grading fee.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '32px', fontFamily: "'Segoe UI', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { color: '#ffffff', fontSize: '24px', fontWeight: '700', margin: '0 0 4px 0' },
  subtitle: { color: '#888888', fontSize: '14px', margin: '0' },
  exportBtn: { backgroundColor: '#1a3a1a', border: '1px solid #22c55e', color: '#22c55e', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  exportBtnDisabled: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#666666', padding: '10px 20px', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px' },
  eventSelector: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '8px' },
  eventSelect: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '12px 16px', color: '#ffffff', fontSize: '15px', outline: 'none', cursor: 'pointer', maxWidth: '600px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px' },
  statLabel: { color: '#888888', fontSize: '12px', margin: '0 0 8px 0', fontWeight: '500' },
  statValue: { fontSize: '28px', fontWeight: '700', margin: '0' },
  statSub: { color: '#666666', fontSize: '11px', margin: '4px 0 0 0' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  tab: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#888888', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  tabActive: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#ffffff', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  retryBtn: { marginLeft: '12px', backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { backgroundColor: '#2d1a1a', border: '1px solid #cc3333', color: '#ff6666', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', alignItems: 'center' },
  warningBox: { backgroundColor: '#2d1a0d', border: '1px solid #f59e0b', color: '#f59e0b', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center' },
  loadingBox: { color: '#888888', textAlign: 'center', padding: '48px' },
  noEventBox: { textAlign: 'center', padding: '80px 32px' },
  filtersCard: { backgroundColor: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  filtersRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' },
  filterLabel: { color: '#888888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchInput: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', outline: 'none', minWidth: '220px' },
  multiSelectBtn: { backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '10px 16px', color: '#ffffff', fontSize: '14px', cursor: 'pointer', textAlign: 'left', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  multiSelectDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2a2a2a', border: '1px solid #3a3a3a', borderRadius: '8px', padding: '8px', zIndex: 100, maxHeight: '200px', overflowY: 'auto', marginTop: '4px', minWidth: '180px' },
  checkboxLabel: { display: 'flex', alignItems: 'center', color: '#cccccc', fontSize: '13px', padding: '6px 8px', cursor: 'pointer', borderRadius: '4px' },
  clearBtn: { backgroundColor: '#2a0000', border: '1px solid #cc0000', color: '#cc0000', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  tableWrapper: { overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 460px)', borderRadius: '12px', border: '1px solid #2a2a2a', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a1a1a', color: '#888888', padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2a2a2a', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 },
  trEven: { backgroundColor: '#0f0f0f' },
  trOdd: { backgroundColor: '#111111' },
  td: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap' },
  tdNum: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap', textAlign: 'right' },
  tdCentre: { padding: '12px 16px', color: '#cccccc', borderBottom: '1px solid #1a1a1a', whiteSpace: 'nowrap', textAlign: 'center' },
  footerRow: { backgroundColor: '#1a1a1a', borderTop: '2px solid #3a3a3a' },
  rankBadge: { backgroundColor: '#1a1a2e', color: '#6b8cff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  targetBadge: { backgroundColor: '#2e1a2e', color: '#cc88ff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  paidBadge: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  unpaidBadge: { backgroundColor: '#2d1a0d', color: '#f59e0b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  approvedBadge: { backgroundColor: '#0d2d1a', color: '#22c55e', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  notApprovedBadge: { backgroundColor: '#2d1a1a', color: '#cc0000', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' },
  emptyBox: { color: '#888888', textAlign: 'center', padding: '48px', backgroundColor: '#0f0f0f' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { color: '#888888', fontSize: '14px' },
  pageButtons: { display: 'flex', gap: '8px', alignItems: 'center' },
  pageBtn: { backgroundColor: '#1a1a1a', border: '1px solid #3a3a3a', color: '#ffffff', padding: '8px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  pageBtnDisabled: { backgroundColor: '#111111', border: '1px solid #222222', color: '#444444', padding: '8px 14px', borderRadius: '6px', cursor: 'not-allowed', fontSize: '13px' },
  pageNumber: { color: '#888888', fontSize: '13px', padding: '0 8px' },
};

export default Registration;